// mexicoV2Copy.ts
// ─── Astria Mexico v2 — bilingual (EN/ES) UI copy ──────────────────────────
// All fixed UI strings for the shell header, tab bar, and the tab screen's
// chrome (loading/reset/submit/CTA text). The client-approved backend copy
// pack response text itself is NOT translated here — it comes back in
// Mexican Spanish from helper/astriaMexicoV2Service.js by default, or in
// English when `wizard.lang === "en"` (see that file's EN_COPY_PACK /
// MX_COPY_PACK). This file only localizes the surrounding UI chrome, and
// its `lang` toggle is the same value sent to the backend as
// `wizard.lang` — see MexicoV2Tab.tsx.
//
// Tab bar labels use the client's MX v2 UI JSON nav names where available
// (Camino Claro, Clima Emocional, Retrato Interior, Ritmo del Tiempo, Tu
// Nota); "Equilibrio Emocional" fills the one data tab (emotion_balance)
// that has no direct nav label in the client's 5-tab nav array.
import { MexicoV2Lang } from "./mexicoV2Language";
import { AstriaMexicoV2Tab } from "./AstriaMexicoV2Shell";

interface ShellCopy {
  title: string;
  subtitle: string;
  langToggleLabel: string;
}

interface TabBarCopy {
  label: string;
  sublabel: string;
}

interface TabScreenCopy {
  question: string;
  helper: string;
  fields: Record<string, { label: string; options?: Record<string, string>; placeholder?: string; lowLabel?: string; highLabel?: string }>;
}

interface MexicoV2CopyShape {
  shell: ShellCopy;
  tabBar: Record<AstriaMexicoV2Tab, TabBarCopy>;
  screens: Record<AstriaMexicoV2Tab, TabScreenCopy>;
  submitButton: string;
  loadingLabel: string;
  resetLink: string;
}

export const MEXICO_V2_COPY: Record<MexicoV2Lang, MexicoV2CopyShape> = {
  es: {
    shell: {
      title: "Astria México",
      subtitle: "Un espacio cálido y ligero para reconectar con lo que sientes",
      langToggleLabel: "Idioma",
    },
    tabBar: {
      soft_summary: { label: "Retrato Interior", sublabel: "Retrato Interior" },
      connection_atmosphere: { label: "Clima Emocional", sublabel: "Clima Emocional" },
      timing_rhythm: { label: "Ritmo del Tiempo", sublabel: "Ritmo del Tiempo" },
      emotion_balance: { label: "Equilibrio Emocional", sublabel: "Equilibrio Emocional" },
      guidance_path: { label: "Camino Claro", sublabel: "Camino Claro" },
      your_note: { label: "Tu Nota", sublabel: "Tu Nota" },
    },
    screens: {
      soft_summary: {
        question: "¿Cómo te sientes en este momento?",
        helper: "Un momento ligero para ver cómo te sientes ahora.",
        fields: {
          mood: { label: "Sentimiento" },
          intensity: {
            label: "Intensidad de ese sentimiento",
            lowLabel: "Ligero",
            highLabel: "Fuerte",
          },
        },
      },
      connection_atmosphere: {
        question: "¿Cómo está el ambiente entre ustedes dos?",
        helper: "La calidez (o la distancia) que sientes con esa persona.",
        fields: {
          connection: {
            label: "Ambiente entre ustedes",
            options: {
              warm: "Cálido",
              distant: "Distante",
              tense: "Tenso",
              playful: "Ligero y divertido",
              quiet: "Callado",
            },
          },
          tone: {
            label: "El tono de la otra persona",
            options: {
              gentle: "Amable",
              guarded: "Reservado",
              open: "Abierto",
              unsure: "Incierto",
            },
          },
        },
      },
      timing_rhythm: {
        question: "¿Cuál es el ritmo de este momento?",
        helper: "El paso y la disposición de este momento.",
        fields: {
          flow: {
            label: "Ritmo actual",
            options: {
              slow: "Lento",
              steady: "Estable",
              rushed: "Apresurado",
              uncertain: "Incierto",
            },
          },
          readiness: {
            label: "Qué tan listo te sientes",
            lowLabel: "Todavía no",
            highLabel: "Listo",
          },
        },
      },
      emotion_balance: {
        question: "¿Dónde están tus emociones ahora?",
        helper: "Dónde se están asentando tus emociones ahora mismo.",
        fields: {
          emotion_zone: {
            label: "Zona emocional",
            options: {
              warm: "Cálida",
              heavy: "Pesada",
              light: "Ligera",
              tender: "Sensible",
              steady: "Estable",
            },
          },
          location: {
            label: "¿Dónde lo sientes más?",
            options: {
              chest: "Pecho",
              mind: "Mente",
              stomach: "Estómago",
              everywhere: "En todo el cuerpo",
            },
          },
        },
      },
      guidance_path: {
        question: "¿Qué está pasando?",
        helper: "Un siguiente paso ligero para lo que estás enfrentando.",
        fields: {
          issue: {
            label: "Qué está pasando",
            options: {
              communication: "Comunicación",
              distance: "Distancia",
              uncertainty: "Incertidumbre",
              overwhelm: "Sobrecarga",
            },
          },
          desired: {
            label: "¿Qué te gustaría sentir?",
            options: {
              clarity: "Claridad",
              closeness: "Cercanía",
              peace: "Paz",
              courage: "Valor",
            },
          },
        },
      },
      your_note: {
        question: "¿Qué tienes en mente?",
        helper: "Una respuesta ligera a tu último mensaje.",
        fields: {
          latest_message: {
            label: "Tu último mensaje",
            placeholder: "¿Qué te gustaría decir?",
          },
          feeling: {
            label: "¿Cómo te sentiste al escribirlo?",
            options: {
              soft: "Ligero",
              hopeful: "Esperanzado",
              unsure: "Incierto",
              relieved: "Aliviado",
            },
          },
        },
      },
    },
    submitButton: "Ver resultado",
    loadingLabel: "Leyendo lo que sientes…",
    resetLink: "Comenzar un nuevo check-in",
  },
  en: {
    shell: {
      title: "Astria Mexico",
      subtitle: "A warm, light space to check in with how you're feeling",
      langToggleLabel: "Language",
    },
    tabBar: {
      soft_summary: { label: "Soft Summary", sublabel: "Retrato Interior" },
      connection_atmosphere: { label: "Connection Atmosphere", sublabel: "Clima Emocional" },
      timing_rhythm: { label: "Timing Rhythm", sublabel: "Ritmo del Tiempo" },
      emotion_balance: { label: "Emotion Balance", sublabel: "Equilibrio Emocional" },
      guidance_path: { label: "Guidance Path", sublabel: "Camino Claro" },
      your_note: { label: "Your Note", sublabel: "Tu Nota" },
    },
    screens: {
      soft_summary: {
        question: "How are you feeling right now?",
        helper: "A light check-in on how you're feeling right now.",
        fields: {
          mood: { label: "Mood" },
          intensity: {
            label: "How strong is that feeling?",
            lowLabel: "Light",
            highLabel: "Strong",
          },
        },
      },
      connection_atmosphere: {
        question: "What's the atmosphere between you two?",
        helper: "The warmth (or distance) you're feeling with someone else.",
        fields: {
          connection: {
            label: "Atmosphere between you two",
            options: {
              warm: "Warm",
              distant: "Distant",
              tense: "Tense",
              playful: "Playful",
              quiet: "Quiet",
            },
          },
          tone: {
            label: "Their tone",
            options: {
              gentle: "Gentle",
              guarded: "Guarded",
              open: "Open",
              unsure: "Unsure",
            },
          },
        },
      },
      timing_rhythm: {
        question: "What's the rhythm of this moment?",
        helper: "The pace and readiness of this moment.",
        fields: {
          flow: {
            label: "Current flow",
            options: {
              slow: "Slow",
              steady: "Steady",
              rushed: "Rushed",
              uncertain: "Uncertain",
            },
          },
          readiness: {
            label: "How ready do you feel?",
            lowLabel: "Not yet",
            highLabel: "Ready",
          },
        },
      },
      emotion_balance: {
        question: "Where are your emotions sitting?",
        helper: "Where your emotions are sitting right now.",
        fields: {
          emotion_zone: {
            label: "Emotion zone",
            options: {
              warm: "Warm",
              heavy: "Heavy",
              light: "Light",
              tender: "Tender",
              steady: "Steady",
            },
          },
          location: {
            label: "Where does it show up most?",
            options: {
              chest: "Chest",
              mind: "Mind",
              stomach: "Stomach",
              everywhere: "Everywhere",
            },
          },
        },
      },
      guidance_path: {
        question: "What's going on?",
        helper: "A soft next step for what you're facing.",
        fields: {
          issue: {
            label: "What's going on",
            options: {
              communication: "Communication",
              distance: "Distance",
              uncertainty: "Uncertainty",
              overwhelm: "Overwhelm",
            },
          },
          desired: {
            label: "What do you want?",
            options: {
              clarity: "Clarity",
              closeness: "Closeness",
              peace: "Peace",
              courage: "Courage",
            },
          },
        },
      },
      your_note: {
        question: "What's on your mind?",
        helper: "A gentle reflection on your latest message.",
        fields: {
          latest_message: {
            label: "Your latest message",
            placeholder: "What would you like to say?",
          },
          feeling: {
            label: "How did you feel while writing it?",
            options: {
              soft: "Soft",
              hopeful: "Hopeful",
              unsure: "Unsure",
              relieved: "Relieved",
            },
          },
        },
      },
    },
    submitButton: "Reveal",
    loadingLabel: "Reading your check-in…",
    resetLink: "Start a new check-in",
  },
};
